import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as ValidateIcon, Transform as TransformIcon,
  Code as ScriptIcon, Api as ApiIcon, Event as EventIcon,
  Rule as ConditionIcon,
} from '@mui/icons-material';

const STEP_TYPES = [
  { value: 'VALIDATE', label: 'Validate', icon: ValidateIcon, color: '#2196f3' },
  { value: 'TRANSFORM', label: 'Transform', icon: TransformIcon, color: '#ff9800' },
  { value: 'SCRIPT', label: 'Script', icon: ScriptIcon, color: '#4caf50' },
  { value: 'API_CALL', label: 'API Call', icon: ApiIcon, color: '#9c27b0' },
  { value: 'PUBLISH_EVENT', label: 'Event', icon: EventIcon, color: '#00bcd4' },
  { value: 'CONDITION', label: 'Condition', icon: ConditionIcon, color: '#ff5722' },
  { value: 'LOG', label: 'Log', icon: ScriptIcon, color: '#607d8b' },
];

const ERROR_ACTIONS = [
  { value: 'FAIL', label: 'Stop Flow' },
  { value: 'SKIP_STEP', label: 'Skip Step' },
  { value: 'RETRY', label: 'Retry Step' },
];

const getStepConfig = (type) => STEP_TYPES.find(s => s.value === type) || STEP_TYPES[0];

/**
 * Horizontal step pipeline designer.
 *
 * Props:
 *   steps: [{ stepCode, stepName, stepOrder, stepType, stepConfig, onErrorAction }]
 *   onChange: (newSteps) => void
 *   colors: theme colors
 */
const FlowStepDesigner = ({ steps = [], onChange, colors }) => {
  const [editStep, setEditStep] = useState(null); // index being edited
  const [editForm, setEditForm] = useState({});

  const sorted = [...steps].sort((a, b) => (a.stepOrder || 0) - (b.stepOrder || 0));

  const handleAddStep = () => {
    const newStep = {
      stepCode: `step_${steps.length + 1}`,
      stepName: `Step ${steps.length + 1}`,
      stepOrder: steps.length + 1,
      stepType: 'SCRIPT',
      stepConfig: {},
      onErrorAction: 'FAIL',
      isActive: true,
    };
    onChange([...steps, newStep]);
    setEditStep(steps.length);
    setEditForm(newStep);
  };

  const handleEditStep = (index) => {
    setEditStep(index);
    setEditForm({ ...sorted[index] });
  };

  const handleSaveStep = () => {
    const updated = [...sorted];
    updated[editStep] = { ...editForm, stepOrder: editStep + 1 };
    onChange(updated);
    setEditStep(null);
  };

  const handleDeleteStep = (index) => {
    const updated = sorted.filter((_, i) => i !== index)
      .map((s, i) => ({ ...s, stepOrder: i + 1 }));
    onChange(updated);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
          Processing Flow ({sorted.length} steps)
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddStep}
          sx={{ textTransform: 'none', color: colors.accent, fontSize: '0.75rem' }}>
          Add Step
        </Button>
      </Box>

      {/* Horizontal step pipeline */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', py: 1, px: 0.5,
        minHeight: 80,
      }}>
        {sorted.length === 0 && (
          <Box sx={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `1px dashed ${colors.border}`, borderRadius: 1, py: 3,
          }}>
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              No steps defined. Click "Add Step" to build the processing pipeline.
            </Typography>
          </Box>
        )}

        {sorted.map((step, index) => {
          const cfg = getStepConfig(step.stepType);
          const StepIcon = cfg.icon;
          return (
            <React.Fragment key={index}>
              {/* Step card */}
              <Box
                onClick={() => handleEditStep(index)}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  minWidth: 120, maxWidth: 150, p: 1.5,
                  border: `1px solid ${colors.border}`, borderRadius: 1,
                  bgcolor: colors.surface, cursor: 'pointer',
                  '&:hover': { borderColor: cfg.color, bgcolor: `${cfg.color}08` },
                  position: 'relative',
                }}
              >
                {/* Delete button */}
                <Tooltip title="Remove step">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteStep(index); }}
                    sx={{ position: 'absolute', top: 2, right: 2, p: 0.25, color: colors.textMuted, '&:hover': { color: colors.error } }}>
                    <DeleteIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>

                {/* Step number */}
                <Chip label={index + 1} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', mb: 0.5, bgcolor: `${cfg.color}22`, color: cfg.color, fontWeight: 600 }} />

                {/* Icon */}
                <StepIcon sx={{ fontSize: 24, color: cfg.color, mb: 0.5 }} />

                {/* Name */}
                <Typography variant="caption" sx={{
                  color: colors.text, fontWeight: 500, fontSize: '0.7rem',
                  textAlign: 'center', lineHeight: 1.2, maxWidth: '100%',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {step.stepName || step.stepCode}
                </Typography>

                {/* Type badge */}
                <Typography variant="caption" sx={{ color: cfg.color, fontSize: '0.6rem', mt: 0.25 }}>
                  {cfg.label}
                </Typography>

                {/* Script/config hint */}
                {step.stepConfig?.scriptName && (
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', fontFamily: 'monospace' }}>
                    {step.stepConfig.scriptName}
                  </Typography>
                )}
                {step.stepConfig?.script && (
                  <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', fontFamily: 'monospace' }}>
                    {step.stepConfig.script}
                  </Typography>
                )}
              </Box>

              {/* Arrow between steps */}
              {index < sorted.length - 1 && (
                <ArrowIcon sx={{ color: colors.border, fontSize: 20, mx: 0.5, flexShrink: 0 }} />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      {/* Edit Step Dialog */}
      <Dialog
        open={editStep !== null}
        onClose={() => setEditStep(null)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: colors.surface, color: colors.text } }}
      >
        <DialogTitle sx={{ color: colors.text, borderBottom: `1px solid ${colors.border}`, pb: 1 }}>
          {editForm.stepName || 'Edit Step'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth size="small" label="Step Code" value={editForm.stepCode || ''}
              onChange={(e) => setEditForm(p => ({ ...p, stepCode: e.target.value }))}
              sx={{ '& .MuiInputBase-input': { color: colors.text, fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />

            <TextField fullWidth size="small" label="Step Name" value={editForm.stepName || ''}
              onChange={(e) => setEditForm(p => ({ ...p, stepName: e.target.value }))}
              sx={{ '& .MuiInputBase-input': { color: colors.text }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />

            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.textSecondary }}>Step Type</InputLabel>
              <Select value={editForm.stepType || 'SCRIPT'} label="Step Type"
                onChange={(e) => setEditForm(p => ({ ...p, stepType: e.target.value }))}
                sx={{ color: colors.text }}>
                {STEP_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Conditional fields based on step type */}
            {(editForm.stepType === 'SCRIPT' || editForm.stepType === 'TRANSFORM') && (
              <>
                <TextField fullWidth size="small"
                  label={editForm.stepType === 'SCRIPT' ? 'Script Name' : 'Transform Script'}
                  value={editForm.stepConfig?.scriptName || editForm.stepConfig?.script || ''}
                  onChange={(e) => setEditForm(p => ({
                    ...p, stepConfig: {
                      ...p.stepConfig,
                      [p.stepType === 'SCRIPT' ? 'scriptName' : 'script']: e.target.value
                    }
                  }))}
                  placeholder="e.g., create_receipt_header"
                  sx={{ '& .MuiInputBase-input': { color: colors.text, fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField fullWidth size="small" label="Input Variable"
                    value={editForm.stepConfig?.inputVar || ''}
                    onChange={(e) => setEditForm(p => ({ ...p, stepConfig: { ...p.stepConfig, inputVar: e.target.value } }))}
                    placeholder="e.g., receiptHeader"
                    helperText="Flow context variable to pass as _payload"
                    sx={{ '& .MuiInputBase-input': { color: colors.text, fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiFormHelperText-root': { color: colors.textMuted } }} />
                  <TextField fullWidth size="small" label="Output Variable"
                    value={editForm.stepConfig?.outputVar || ''}
                    onChange={(e) => setEditForm(p => ({ ...p, stepConfig: { ...p.stepConfig, outputVar: e.target.value } }))}
                    placeholder="e.g., receiptResult"
                    helperText="Store step result in flow context as"
                    sx={{ '& .MuiInputBase-input': { color: colors.text, fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary }, '& .MuiFormHelperText-root': { color: colors.textMuted } }} />
                </Box>
              </>
            )}

            {editForm.stepType === 'API_CALL' && (
              <TextField fullWidth size="small" label="API Key (from api_registry)"
                value={editForm.stepConfig?.apiKey || ''}
                onChange={(e) => setEditForm(p => ({ ...p, stepConfig: { ...p.stepConfig, apiKey: e.target.value } }))}
                placeholder="e.g., receiving.create_receipt"
                sx={{ '& .MuiInputBase-input': { color: colors.text, fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />
            )}

            {editForm.stepType === 'PUBLISH_EVENT' && (
              <TextField fullWidth size="small" label="Event Code"
                value={editForm.stepConfig?.eventCode || ''}
                onChange={(e) => setEditForm(p => ({ ...p, stepConfig: { ...p.stepConfig, eventCode: e.target.value } }))}
                placeholder="e.g., RECEIPT_CREATED"
                sx={{ '& .MuiInputBase-input': { color: colors.text, fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />
            )}

            {editForm.stepType === 'CONDITION' && (
              <TextField fullWidth size="small" label="Condition Expression"
                value={editForm.conditionExpression || ''}
                onChange={(e) => setEditForm(p => ({ ...p, conditionExpression: e.target.value }))}
                placeholder='e.g., payload.priority == "HIGH"'
                sx={{ '& .MuiInputBase-input': { color: colors.text, fontFamily: 'monospace' }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />
            )}

            {editForm.stepType === 'LOG' && (
              <TextField fullWidth size="small" label="Log Message"
                value={editForm.stepConfig?.message || ''}
                onChange={(e) => setEditForm(p => ({ ...p, stepConfig: { ...p.stepConfig, message: e.target.value } }))}
                placeholder="e.g., Receipt created successfully"
                sx={{ '& .MuiInputBase-input': { color: colors.text }, '& .MuiInputLabel-root': { color: colors.textSecondary } }} />
            )}

            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: colors.textSecondary }}>On Error</InputLabel>
              <Select value={editForm.onErrorAction || 'FAIL'} label="On Error"
                onChange={(e) => setEditForm(p => ({ ...p, onErrorAction: e.target.value }))}
                sx={{ color: colors.text }}>
                {ERROR_ACTIONS.map(a => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, p: 1.5 }}>
          <Button onClick={() => setEditStep(null)} sx={{ color: colors.textSecondary }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveStep}
            sx={{ bgcolor: colors.success, textTransform: 'none' }}>
            Save Step
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlowStepDesigner;
